import React, { Suspense, lazy, useId, useRef, useState } from "react";
import { Eye, EyeOff, FileText, Loader2, RefreshCw, Upload } from "lucide-react";
import {
  AppButton, DropZone, FieldError, FormField, HelpText, Input, Label, NativeSelect, Radio, Textarea,
} from "../ui";
import Shimmer from "../Common/Shimmer";
import { QuizImageField } from "./QuizImageField";
import { formatBytes, uploadPassagePdf } from "./quizMedia";

/**
 * PassageForm — the fields of a passage, controlled. The passage page and the
 * question editor's "New passage" dialog both render it, so the two can never
 * ask for different things.
 *
 * Props:
 *   value      — { title, kind, instructions, text, imageUrl, file, category, subcategory }
 *   onChange   — (patch) => void
 *   categories — the bank's flat category list (optional; hides the field when absent)
 *   loadFileUrl — () => Promise<string> for the saved/uploaded PDF's preview
 */

// pdf.js loads only when a document is previewed.
const PassagePdf = lazy(() => import("../QuizPassage/PassagePdf"));

export const TEXT_MAX = 40000;

export const emptyPassage = () => ({
  title: "",
  kind: "text",
  instructions: "",
  text: "",
  imageUrl: "",
  file: null,
  category: "",
  subcategory: "",
});

/** The client-side half of the server's checks — the server has the last word. */
export function passageProblem(p) {
  if (!p.title.trim()) return "Give the passage a title.";
  if (p.kind === "pdf" && !p.file?.key) return "Upload the PDF this passage is about.";
  if (p.kind === "text" && !p.text.trim()) return "Write the passage the questions are about.";
  if (p.text.length > TEXT_MAX) return "The passage is too long — upload it as a PDF instead.";
  return "";
}

export const passagePayload = (p) => ({
  title: p.title,
  kind: p.kind,
  instructions: p.instructions,
  text: p.kind === "text" ? p.text : "",
  imageUrl: p.kind === "text" ? p.imageUrl : "",
  file: p.kind === "pdf" ? p.file : null,
  category: p.category || null,
  subcategory: p.subcategory || null,
});

function PdfField({ file, onChange, loadFileUrl }) {
  const inputRef = useRef(null);
  const id = useId();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [drag, setDrag] = useState(false);
  const [preview, setPreview] = useState(false);

  const upload = async (picked) => {
    if (!picked) return;
    setBusy(true);
    setError("");
    try {
      onChange(await uploadPassagePdf(picked));
      setPreview(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  const onDrag = (e) => {
    e.preventDefault();
    if (!busy) setDrag(e.type === "dragenter" || e.type === "dragover");
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-1.5">
      <Label id={`${id}-label`} required>Document</Label>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        className="tw-hidden"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; upload(f); }}
      />
      {file?.key ? (
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-2 tw-rounded-sm tw-border tw-border-solid tw-border-line tw-bg-surface tw-px-3 tw-py-2.5">
          <FileText size={18} className="tw-shrink-0 tw-text-content-muted" aria-hidden="true" />
          <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-sm tw-text-content" title={file.name}>
            {file.name}
            {file.size ? <span className="tw-text-content-muted"> · {formatBytes(file.size)}</span> : null}
          </span>
          {loadFileUrl && (
            <AppButton type="button" variant="ghost" size="sm" onClick={() => setPreview((v) => !v)} aria-expanded={preview}>
              {preview ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
              {preview ? "Hide preview" : "Preview"}
            </AppButton>
          )}
          <AppButton type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={busy}>
            {busy ? <Loader2 size={16} className="tw-animate-spin" aria-hidden="true" /> : <RefreshCw size={16} aria-hidden="true" />}
            {busy ? "Uploading…" : "Replace"}
          </AppButton>
        </div>
      ) : (
        <DropZone
          size="sm"
          state={drag ? "active" : "idle"}
          disabled={busy}
          aria-labelledby={`${id}-label`}
          aria-describedby={`${id}-help`}
          onClick={() => inputRef.current?.click()}
          onDragEnter={onDrag}
          onDragOver={onDrag}
          onDragLeave={onDrag}
          onDrop={(e) => { e.preventDefault(); setDrag(false); if (!busy) upload(e.dataTransfer.files?.[0]); }}
        >
          {busy
            ? <Loader2 size={18} className="tw-animate-spin tw-text-content-muted" aria-hidden="true" />
            : <Upload size={18} className="tw-text-content-subtle group-hover:tw-text-primary" aria-hidden="true" />}
          <span className="tw-text-sm tw-font-medium tw-text-content">
            {busy ? "Uploading…" : "Upload the PDF, or drop it here"}
          </span>
        </DropZone>
      )}
      <HelpText id={`${id}-help`}>
        PDF up to 10 MB. Students read it inside the exam; it is never offered as a download.
      </HelpText>
      {error && <FieldError>{error}</FieldError>}
      {preview && file?.key && loadFileUrl && (
        <div className="tw-mt-1 tw-max-h-[32rem] tw-overflow-y-auto tw-rounded-sm tw-border tw-border-solid tw-border-line tw-p-3">
          <Suspense fallback={<Shimmer height={360} />}>
            <PassagePdf loadUrl={loadFileUrl} cacheKey={`editor:${file.key}`} fileName={file.name} />
          </Suspense>
        </div>
      )}
    </div>
  );
}

export default function PassageForm({ value, onChange, categories, loadFileUrl }) {
  const top = (categories || []).filter((c) => !c.parentCategory);
  const subs = (categories || []).filter((c) => value.category && String(c.parentCategory) === String(value.category));

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <FormField label="Title" required hint="For you and for students — e.g. “Discharge summary: Case 4”">
        <Input
          size="lg"
          value={value.title}
          maxLength={200}
          onChange={(e) => onChange({ title: e.target.value })}
          placeholder="Passage title"
        />
      </FormField>

      <fieldset className="tw-m-0 tw-flex tw-flex-col tw-gap-1.5 tw-border-0 tw-p-0">
        <legend className="tw-mb-1.5 tw-p-0 tw-text-[0.8125rem] tw-font-semibold tw-text-content">What students read</legend>
        <div className="tw-grid tw-gap-2 sm:tw-grid-cols-2">
          <Radio
            variant="card"
            name="passage-kind"
            checked={value.kind === "text"}
            onChange={() => onChange({ kind: "text" })}
            label="A passage"
            description="Text you type or paste — a paragraph, a case note, a dialogue. Can include a figure."
          />
          <Radio
            variant="card"
            name="passage-kind"
            checked={value.kind === "pdf"}
            onChange={() => onChange({ kind: "pdf" })}
            label="A document (PDF)"
            description="A file read inside the exam — a patient chart, a report, a form."
          />
        </div>
      </fieldset>

      <FormField label="Instructions" hint="Optional. Shown above the passage.">
        <Textarea
          rows={2}
          value={value.instructions}
          maxLength={1000}
          onChange={(e) => onChange({ instructions: e.target.value })}
          placeholder="Read the passage and answer the questions that follow."
        />
      </FormField>

      {value.kind === "text" ? (
        <>
          <FormField
            label="Passage"
            required
            hint={`${value.text.length.toLocaleString("en-IN")} / ${TEXT_MAX.toLocaleString("en-IN")} characters · line breaks are kept`}
            error={value.text.length > TEXT_MAX ? "Too long — upload it as a PDF instead." : undefined}
          >
            <Textarea
              rows={12}
              value={value.text}
              onChange={(e) => onChange({ text: e.target.value })}
              placeholder="Paste or type the passage here…"
              className="tw-leading-relaxed"
            />
          </FormField>
          <QuizImageField
            label="Figure (optional)"
            hint="A chart, table or picture the questions refer to"
            value={value.imageUrl}
            onChange={(url) => onChange({ imageUrl: url })}
          />
        </>
      ) : (
        <PdfField file={value.file} onChange={(file) => onChange({ file })} loadFileUrl={loadFileUrl} />
      )}

      {categories && (
        <div className="tw-grid tw-gap-3 sm:tw-grid-cols-2">
          <FormField label="Category" hint="Optional. New questions in this set start here.">
            <NativeSelect
              value={value.category || ""}
              onChange={(e) => onChange({ category: e.target.value, subcategory: "" })}
            >
              <option value="">None</option>
              {top.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
            </NativeSelect>
          </FormField>
          <FormField label="Subcategory">
            <NativeSelect
              value={value.subcategory || ""}
              onChange={(e) => onChange({ subcategory: e.target.value })}
              disabled={!subs.length}
            >
              <option value="">{subs.length ? "None" : "—"}</option>
              {subs.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
            </NativeSelect>
          </FormField>
        </div>
      )}
    </div>
  );
}
